import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Collapse,
  Stack,
  Typography,
} from '@mui/material';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { FsokPaper } from '../UIComponents/FsokPaper/FsokPaper';
import { hashCode } from '../common/miscFunctions';
import { ColumnDefinition } from '../common/table/ColumnDefinition';
import Table from '../common/table/Table';
import { LoadState } from '../types/LoadState';
import { RegisterbeteckningInput } from './RegisterbeteckningInput';
import { FastighetRow } from './types/FastighetRow';

interface ValdaFastigheterProps {
  loadState: LoadState;
  registerbeteckningar: string[];
  setRegisterbeteckningar: (value: string[]) => void;
  pageState: [number, React.Dispatch<React.SetStateAction<number>>];
}

export const ValdaFastigheter: React.FC<ValdaFastigheterProps> = ({
  loadState,
  registerbeteckningar,
  setRegisterbeteckningar,
  pageState,
}) => {
  const [open, setOpen] = useState(!!registerbeteckningar.length);
  const [tableData, setTableData] = useState<FastighetRow[]>([]);
  const [numOfRows, setNumOfRows] = useState(0);
  const columnHeaders: Record<string, string>[] = [
    {
      id: 'fastighet',
      header: 'Fastighetsbeteckning eller UUID',
    },
  ];

  const columnDefinitions: ColumnDefinition<FastighetRow>[] = [
    {
      id: 'fastighet',
      header: 'Fastighetsbeteckning eller UUID',
    },
    {
      id: 'emptyForBin',
      header: '',
    },
  ];

  const getCountString = (count: number) =>
    `${!count ? 'Inga' : count} fastighet${count === 1 ? '' : 'er'} vald${
      count === 1 ? '' : 'a'
    }`;

  useEffect(() => {
    const fastigheter: FastighetRow[] = registerbeteckningar.map((f) => ({
      id: hashCode(f),
      fastighet: f,
    }));
    setTableData(fastigheter);
  }, [registerbeteckningar]);

  useEffect(() => {
    const n = registerbeteckningar.length;
    n > 12 ? setNumOfRows(12) : setNumOfRows(n);
  }, [registerbeteckningar]);

  const handleDelete = async (ids: number[]): Promise<void> => {
    setRegisterbeteckningar(
      registerbeteckningar.filter((f) => !ids.includes(hashCode(f)))
    );
  };

  return (
    <FsokPaper style={{ width: '100%' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={0}
        onClick={() => setOpen(!open)}
        sx={{ pb: 1, pt: 1, pr: 1 }}
      >
        <Box
          sx={{ width: '100%', justifyContent: 'center', cursor: 'pointer' }}
        >
          <Typography variant="h6" sx={{ textAlign: 'center' }}>
            {loadState === LoadState.Loading ? (
              <>
                <CircularProgress size={16} /> Fastigheter läses in från fil...
              </>
            ) : (
              getCountString(registerbeteckningar.length)
            )}
          </Typography>
        </Box>
        {open ? <ExpandLess /> : <ExpandMore />}
      </Stack>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <RegisterbeteckningInput
          registerbeteckningar={registerbeteckningar}
          setRegisterbeteckningar={setRegisterbeteckningar}
        />
        <Table
          data={tableData}
          rowsPerPage={numOfRows}
          columnHeaders={columnHeaders}
          columnDefinitions={columnDefinitions}
          deleteMultiIds={handleDelete}
          pageState={pageState}
        />
      </Collapse>
    </FsokPaper>
  );
};
